import { http } from '@lm/constants'
import type {
  FetchBaseQueryMeta,
  QueryReturnValue,
} from '@reduxjs/toolkit/query'
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import camelcaseKeys from 'camelcase-keys'

import { API_URL } from '@/constants/configurations'
import { selectAuth } from '@/services/auth'
import { endpoints } from '@/services/endpoints'
import type { RootState } from '@/state/reducer'
import { logout } from '@/state/slices/authSlice'

import type {
  FetchEkioskQueryError,
  FetchEkioskQuerySuccess,
  FetchEkioskQuerySuccessData,
} from './types'

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (requestHeaders, { getState }) => {
    const { token } = selectAuth(getState() as RootState)

    if (token) {
      requestHeaders.set(
        http.headers.CONTENT_TYPE,
        http.headers.APPLICATION_JSON
      )
      requestHeaders.set(http.headers.ACCESS_TOKEN, token)
    }
    return requestHeaders
  },
})

type ReauthQueryResult<T = FetchEkioskQuerySuccessData> = QueryReturnValue<
  FetchEkioskQuerySuccess<T>,
  FetchEkioskQueryError,
  FetchBaseQueryMeta
>

type BaseQuery = typeof baseQuery

export async function baseQueryWithReauth<T = FetchEkioskQuerySuccessData>(
  args: Parameters<BaseQuery>[0],
  api: Parameters<BaseQuery>[1],
  extraOptions: Parameters<BaseQuery>[2]
): Promise<ReauthQueryResult<T>> {
  const result = (await baseQuery(
    args,
    api,
    extraOptions
  )) as ReauthQueryResult<T>

  if (result.data) {
    result.data = camelcaseKeys(result.data, {
      deep: true,
    }) as FetchEkioskQuerySuccess<T>
  }

  const requestUrl = typeof args === 'string' ? args : args.url

  if (result.error?.status === 401 && requestUrl !== endpoints.SIGN_IN) {
    api.dispatch(logout({ forceLogin: true }))
  }
  if (result.error?.data && typeof result.error.data === 'object') {
    result.error.data = camelcaseKeys(result.error.data, {
      deep: true,
    })
  }

  return result
}
